// i18next-extract-mark-ns-start case-studies

import React, { FC, useEffect, useRef, useState } from 'react'
import SectionHeader from 'src/components/ui/SectionHeader'
import FormattedHtmlContent from 'src/components/ui/FormattedHtmlContent'
import { directusFileUrl } from 'src/helpers/directus'
import { gsap, ScrollTrigger } from 'gsap/all'
import { createShowFromBottomAnimation } from 'src/helpers/animations'
import Layout from 'src/components/layout'
import { Helmet } from 'react-helmet'
import WebinarSection from 'src/components/sections/webinar'
import { graphql } from 'gatsby'
import ContactSection from 'src/components/sections/ContactSection'
import styled from 'styled-components'
import getPlaceholderImages from 'src/helpers/caseStudyImages'
import { GLOBAL_PREVIEW_MODE } from 'src/helpers/preview'

const TemplateImageContainer = styled.div<{ $isHero?: boolean; $fill?: boolean }>`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 2rem; /* Safe standard fallback for high roundness */
  background-color: var(--color-surface, #f5f5f5);
  
  ${({ $isHero, $fill }) =>
    $isHero
      ? `
      height: 50vh;
      max-height: 900px;
      @media (min-width: 768px) {
        border-radius: 3rem;
        height: 85vh;
      }
    `
      : $fill
      ? `
      height: auto;
      @media (min-width: 768px) {
        border-radius: 3rem;
        height: 100%;
      }
    `
      : `
      height: auto;
      @media (min-width: 768px) {
        border-radius: 3rem;
      }
    `}
`

const AnimatedSection: FC<{
  children: React.ReactNode
  className?: string
  ready?: boolean
}> = ({ children, className, ready = true }) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const timeline = useRef<any>(null)
  const isAnimated = useRef(false)

  useEffect(() => {
    if (!ready || isAnimated.current) return

    if (!timeline.current) {
      timeline.current = gsap.timeline()
    }
    if (elementRef.current) {
      isAnimated.current = true
      createShowFromBottomAnimation(timeline.current, elementRef.current)
    }
  }, [ready])

  return (
    <div ref={elementRef} className={`opacity-0 ${className}`}>
      {children}
    </div>
  )
}

AnimatedSection.defaultProps = {
  className: '',
  ready: true,
}

const AnimatedGridItem: FC<{ children: React.ReactNode; className?: string; index?: number; triggerRef?: React.RefObject<Element> }> = ({
  children,
  className,
  index = 0,
  triggerRef,
}) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const timeline = useRef<any>(null)

  useEffect(() => {
    if (!timeline.current) {
      timeline.current = gsap.timeline()
    }
    if (elementRef.current) {
      const position = index > 0 ? index * 0.16 : undefined
      createShowFromBottomAnimation(
        timeline.current,
        elementRef.current,
        triggerRef?.current || undefined,
        position
      )
    }
  }, [index, triggerRef])

  return (
    <div ref={elementRef} className={`opacity-0 ${className || ''}`}>
      {children}
    </div>
  )
}

interface IGridImageItem {
  id: string
  directus_files_id?: {
    id: string
  }
}

interface ICaseStudyDetailsSectionData {
  id: string
  subtitle: string
  firstPartOfTitle: string
  secondPartOfTitle: string
  content: string
  desktopImage?: {
    id: string
  }
  gridImages?: IGridImageItem[]
}

interface IContributionItemData {
  id: string
  title: string
  content: string
  icon: {
    id: string
  }
}

interface ICaseStudyProps {
  pageContext: {
    slug: string
    platforms: string
    desktopImage: {
      id: string
    }
    translation: {
      subtitle: string
      contentHeader: string
      firstPartOfTitle: string
      secondPartOfTitle: string
      isWebinarDisplayed: boolean
      webinarPresentationLink: string
      webinarVideoLink: string
      excerpt: string
      contribution: string
      sections: ICaseStudyDetailsSectionData[]
      contributionSubtitle: string
      contributionTitleFirstPart: string
      contributionTitleSecondPart: string
      contributionItems: IContributionItemData[]
    }
  }
}

const CaseStudy: FC<ICaseStudyProps> = ({
  pageContext: {
    slug,
    platforms,
    desktopImage,
    translation: {
      contentHeader,
      firstPartOfTitle,
      secondPartOfTitle,
      isWebinarDisplayed,
      webinarPresentationLink,
      webinarVideoLink,
      excerpt,
      contribution,
      sections,
      contributionSubtitle,
      contributionTitleFirstPart,
      contributionTitleSecondPart,
      contributionItems,
    },
  },
}) => {
  const title = `${firstPartOfTitle} ${secondPartOfTitle}`
  const placeholderImages = getPlaceholderImages(slug)
  const heroImageUrl =
    !GLOBAL_PREVIEW_MODE && desktopImage?.id
      ? directusFileUrl(desktopImage.id)
      : placeholderImages[0]

  const [heroImageReady, setHeroImageReady] = useState(false)
  const heroImgRef = useRef<HTMLImageElement>(null)

  const handleHeroImageLoad = () => {
    setHeroImageReady(true)
    ScrollTrigger.refresh()
  }

  useEffect(() => {
    if (heroImgRef.current?.complete && heroImgRef.current?.naturalWidth > 0) {
      setHeroImageReady(true)
      ScrollTrigger.refresh()
      return
    }

    if (heroImgRef.current?.decode) {
      heroImgRef.current
        .decode()
        .then(() => {
          setHeroImageReady(true)
          ScrollTrigger.refresh()
        })
        .catch(() => {
          setHeroImageReady(true)
        })
    }

    const timer = setTimeout(() => {
      setHeroImageReady(true)
      ScrollTrigger.refresh()
    }, 800)

    return () => clearTimeout(timer)
  }, [heroImageUrl])

  const transformedSections = (sections || []).map((s) => {
    if (
      GLOBAL_PREVIEW_MODE &&
      slug === '/latin-dance-passion' &&
      (s.subtitle?.toLowerCase() === 'web platform' || s.firstPartOfTitle?.includes('responsive'))
    ) {
      return {
        ...s,
        firstPartOfTitle: 'clear, responsive',
        secondPartOfTitle: 'and seamless',
      }
    }
    return s
  })

  const hasContributionSection = Boolean(
    contributionItems &&
      contributionItems.length > 0 &&
      (contributionTitleFirstPart ||
        contributionTitleSecondPart ||
        contributionSubtitle ||
        contributionItems.some((item) => item?.title || item?.content))
  )

  const hasText = (idx: number) => {
    const s = transformedSections[idx]
    if (!s) return false
    return !!(s.firstPartOfTitle || s.secondPartOfTitle || s.subtitle || s.content)
  }

  const renderText = (idx: number) => {
    if (!hasText(idx)) return null
    const s = transformedSections[idx]
    const isAlternate = idx % 2 === 1

    if (isAlternate) {
      return (
        <div className="w-full mb-16 grid grid-cols-1 md:grid-cols-12 gap-y-8 md:gap-x-12 items-start">
          {/* Left Column: Subtitle & Titles */}
          <div className="md:col-span-6">
            {s.subtitle && (
              <h4 className="font-palanquinDark text-sm md:text-base font-bold uppercase tracking-widest text-red mb-4">
                {s.subtitle}
              </h4>
            )}
            {(s.firstPartOfTitle || s.secondPartOfTitle) && (
              <h2 className="font-palanquinDark text-4xl md:text-6xl font-bold tracking-tight text-black dark:text-white leading-tight">
                {s.firstPartOfTitle} <span className="text-red block">{s.secondPartOfTitle}</span>
              </h2>
            )}
          </div>

          {/* Right Column: Body Content */}
          <div className={`md:col-span-6 md:pl-12 ${s.subtitle ? 'md:mt-28' : 'md:mt-20'}`}>
            {s.content && (
              <div className="prose prose-lg md:prose-2xl dark:prose-invert max-w-none font-semibold text-black dark:text-white">
                <FormattedHtmlContent
                  className="font-palanquin leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: s.content }}
                />
              </div>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="w-full mb-16">
        {s.subtitle && (
          <h4 className="font-palanquinDark text-sm md:text-base font-bold uppercase tracking-widest text-red mb-4">
            {s.subtitle}
          </h4>
        )}
        {(s.firstPartOfTitle || s.secondPartOfTitle) && (
          <h2 className="font-palanquinDark text-4xl md:text-6xl font-bold tracking-tight text-black dark:text-white leading-tight mb-8">
            {s.firstPartOfTitle} <span className="text-red block">{s.secondPartOfTitle}</span>
          </h2>
        )}

        {s.content && (
          <div className="prose prose-lg md:prose-2xl dark:prose-invert max-w-2xl font-semibold text-black dark:text-white mt-10">
            <FormattedHtmlContent
              className="font-palanquin leading-relaxed"
              dangerouslySetInnerHTML={{ __html: s.content }}
            />
          </div>
        )}
      </div>
    )
  }

  const renderImage = (idx: number) => {
    const s = transformedSections[idx]

    // Determine images list from gridImages if present, falling back to desktopImage or placeholders
    const gridFileIds = (s?.gridImages || [])
      .map((item) => item?.directus_files_id?.id)
      .filter((id): id is string => Boolean(id))

    const imageUrls: string[] = []

    if (!GLOBAL_PREVIEW_MODE && gridFileIds.length > 0) {
      gridFileIds.forEach((fileId) => imageUrls.push(directusFileUrl(fileId)))
    } else if (!GLOBAL_PREVIEW_MODE && s?.desktopImage?.id) {
      imageUrls.push(directusFileUrl(s.desktopImage.id))
    } else {
      const placeholder = placeholderImages[(idx + 1) % placeholderImages.length]
      if (placeholder) imageUrls.push(placeholder)
    }

    if (imageUrls.length === 0) return null

    const renderSingleImage = (
      url: string,
      index: number,
      containerClassName?: string,
      fill?: boolean
    ) => (
      <AnimatedGridItem
        key={url + index}
        index={index}
        className={containerClassName}
      >
        <TemplateImageContainer $fill={fill} className={fill ? 'h-full w-full' : ''}>
          <img
            src={url}
            alt="Section Preview"
            loading="lazy"
            decoding="async"
            onLoad={() => ScrollTrigger.refresh()}
            className={`w-full block ${fill ? 'h-auto md:h-full md:object-cover' : 'h-auto'}`}
          />
        </TemplateImageContainer>
      </AnimatedGridItem>
    )

    // 1 image: full width, height auto
    if (imageUrls.length === 1) {
      return renderSingleImage(imageUrls[0], 0)
    }

    // 2 images: on mobile stacked, on md+ 2 columns with gap
    if (imageUrls.length === 2) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-large w-full">
          {imageUrls.map((url, i) => renderSingleImage(url, i))}
        </div>
      )
    }

    // 3 images: 3x3 grid (2fr 2fr top-left, 2fr 1fr bottom-left, 1fr 3fr right) with large gap
    if (imageUrls.length === 3) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-3 gap-large w-full md:aspect-[4/3] xl:aspect-[16/11]">
          {renderSingleImage(
            imageUrls[0],
            0,
            'md:col-start-1 md:col-span-2 md:row-start-1 md:row-span-2 h-full w-full',
            true
          )}
          {renderSingleImage(
            imageUrls[1],
            1,
            'md:col-start-1 md:col-span-2 md:row-start-3 md:row-span-1 h-full w-full',
            true
          )}
          {renderSingleImage(
            imageUrls[2],
            2,
            'md:col-start-3 md:col-span-1 md:row-start-1 md:row-span-3 h-full w-full',
            true
          )}
        </div>
      )
    }

    // 4 images: 1st full width, next 2 in 2 columns, 4th full width
    if (imageUrls.length === 4) {
      return (
        <div className="flex flex-col gap-large w-full">
          {renderSingleImage(imageUrls[0], 0)}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-large w-full">
            {imageUrls.slice(1, 3).map((url, i) => renderSingleImage(url, i + 1))}
          </div>
          {renderSingleImage(imageUrls[3], 3)}
        </div>
      )
    }

    // Fallback for > 4 images: 1st full width, pairs in 2 columns, last full width if odd
    return (
      <div className="flex flex-col gap-large w-full">
        {renderSingleImage(imageUrls[0], 0)}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-large w-full">
          {imageUrls.slice(1).map((url, i) => renderSingleImage(url, i + 1))}
        </div>
      </div>
    )
  }

  return (
    <Layout title={title} metas={[]}>
      <Helmet>
        {heroImageUrl && <link rel="preload" as="image" href={heroImageUrl} />}
      </Helmet>
      <div className="container py-32 md:py-48">
        {/* HERO HEADER */}
        <AnimatedSection className="grid grid-cols-1 md:grid-cols-12 gap-y-12 items-center">
          {/* LEFT COLUMN: Title & Contribution */}
          <div className="md:col-span-7 space-y-12">
            <SectionHeader
              titlePart1={firstPartOfTitle}
              titlePart2={secondPartOfTitle}
            />

            {contribution && (
              <div className="">
                <p className="font-palanquinDark text-xs font-bold uppercase tracking-widest text-red mb-4">
                  Contribution
                </p>
                <div className="flex flex-wrap gap-x-3 gap-y-2">
                  {contribution.split(',').map((item) => (
                    <span key={item.trim()} className="font-palanquin text-lg font-bold text-black dark:text-white uppercase">
                      {item.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Category & Description */}
          <div className="md:col-span-5 md:pl-12 space-y-8">
            {contentHeader && (
              <p className="font-palanquinDark text-lg md:text-xl font-bold uppercase tracking-widest text-red">
                {contentHeader}
              </p>
            )}

            {excerpt && (
              <div className="max-w-2xl">
                <FormattedHtmlContent
                  dangerouslySetInnerHTML={{ __html: excerpt }}
                  className="font-palanquin font-semibold text-sm md:text-base leading-relaxed text-black dark:text-white"
                />
              </div>
            )}
          </div>
        </AnimatedSection>

        {/* HERO IMAGE */}
        <AnimatedSection ready={heroImageReady} className="w-full mt-16 mb-32 md:mb-48 relative group">
          <TemplateImageContainer $isHero>
            <img
              ref={heroImgRef}
              src={heroImageUrl}
              alt={title}
              loading="eager"
              // @ts-ignore
              fetchPriority="high"
              decoding="async"
              onLoad={handleHeroImageLoad}
              className="w-full h-full object-cover"
            />
          </TemplateImageContainer>

          {/* Platforms/Tags overlapping the image */}
          {platforms && (
            <div className="absolute top-8 right-8 flex flex-wrap gap-2 justify-end z-10 transition-transform duration-500 ease-out transform group-hover:-translate-x-4">
              {platforms.split(',').map((tag) => (
                <span
                  key={tag.trim()}
                  className="rounded-full px-8 py-4 text-md font-bold uppercase tracking-wider text-white"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(24px)',
                    WebkitBackdropFilter: 'blur(24px)'
                  }}
                >
                  {tag.trim()}
                </span>
              ))}
            </div>
          )}
        </AnimatedSection>
        <div className="">
          {sections.map((section, idx) => {
            const blockHasText = hasText(idx)
            let marginClass = ''
            if (idx > 0) {
              marginClass = blockHasText ? 'mt-32 md:mt-48' : 'mt-8'
            }
            return (
              <div key={section.id} className={marginClass}>
                {blockHasText && (
                  <AnimatedSection>
                    {renderText(idx)}
                  </AnimatedSection>
                )}
                <div className={`w-full ${blockHasText ? 'mt-12' : ''}`}>{renderImage(idx)}</div>
              </div>
            )
          })}
        </div>
      </div>

      {hasContributionSection && (
        <section id={contributionSubtitle} data-name={contributionSubtitle} className="container py-10 md:py-20">
          <SectionHeader
            subtitle={contributionSubtitle}
            titlePart1={contributionTitleFirstPart}
            titlePart2={contributionTitleSecondPart}
            listLayout="interactive-rows"
            interactiveRowsVariant="compact"
            listItems={contributionItems.map((item) => ({
              title: item.title,
              content: item.content,
            }))}
          />
        </section>
      )}

      {isWebinarDisplayed && (
        <WebinarSection
          videoEmbedId={webinarVideoLink}
          webinarPresentationLink={webinarPresentationLink}
        />
      )}
      <ContactSection />
    </Layout>
  )
}

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`

export default CaseStudy
